/**
 * FormDialog — vỏ dialog dùng chung cho các form Create/Update.
 * Children là body form; cha tự quản lý form state (RHF).
 *
 * Save/Cancel được đặt trong `DialogTitle` (header) — Access parity với cặp
 * `btnSave/btnCancel` của Sales Contract / Purchase / Futures form dialogs, để
 * mọi form New/Edit trong app có cùng format pin sát phải header.
 */
import type { ReactNode } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { TEXT_TRUNCATE } from '@/theme/responsive'

export interface FormDialogProps {
  open: boolean
  title: string
  loading?: boolean
  submitText?: string
  cancelText?: string
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  onClose: () => void
  onSubmit: () => void
  children: ReactNode
  disableSubmit?: boolean
}

/** Save/Cancel button sx — đồng nhất với Sales Contract / Purchase / Futures forms. */
const headerButtonSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

export function FormDialog({
  open,
  title,
  loading = false,
  submitText = 'Save',
  cancelText = 'Cancel',
  maxWidth = 'sm',
  onClose,
  onSubmit,
  children,
  disableSubmit = false,
}: FormDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title={typeof title === 'string' ? title : undefined}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
        >
          {title}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={onSubmit}
            disabled={loading || disableSubmit}
            sx={headerButtonSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : submitText}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={loading}
            sx={headerButtonSx}
          >
            {cancelText}
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers>
        <Box component="form" noValidate>
          {children}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default FormDialog
